import * as THREE from 'three';
import type { TextureAssetLoader, LoaderManifest } from '../../assets';
import { runtimeAssetUrl } from '../../assets/runtime-asset-url';
import type { EyeExpression, EyeId } from './types';
import { EYE_ID_TO_ASSET_ID } from './types';

export const EYE_EXPRESSIONS: readonly EyeExpression[] = [
  'base',
  'happy',
  'sad',
  'angry',
  'surprised',
  'confused',
  'excited',
  'dead',
] as const;

export interface EyeTextureStore {
  resolve(eyeId: EyeId, expression: EyeExpression): Promise<THREE.Texture | null>;
  retain(eyeId: EyeId, expression: EyeExpression): () => void;
  dispose(): void;
}

export interface EyeTextureStoreConfig {
  readonly manifest: LoaderManifest;
  readonly textureLoader: TextureAssetLoader;
  readonly baseTextures: Partial<Record<EyeId, THREE.Texture>>;
  readonly maxCachedPixels?: number;
}

interface CacheEntry {
  readonly key: string;
  readonly texture: THREE.Texture;
  readonly pixels: number;
  lastUsed: number;
}

export function eyeExpressionAssetId(eyeId: EyeId, expression: EyeExpression): string {
  const baseId = EYE_ID_TO_ASSET_ID[eyeId];
  return expression === 'base' ? baseId : `${baseId}-${expression}`;
}

export function createEyeTextureStore(config: EyeTextureStoreConfig): EyeTextureStore {
  const maxCachedPixels = config.maxCachedPixels ?? 2_000_000;
  const cache = new Map<string, CacheEntry>();
  const pending = new Map<string, Promise<THREE.Texture | null>>();
  const retainCounts = new Map<string, number>();
  let usedPixels = 0;
  let tick = 0;
  let disposed = false;

  function key(eyeId: EyeId, expression: EyeExpression): string {
    return `${eyeId}:${expression}`;
  }

  function baseTexture(eyeId: EyeId): THREE.Texture | null {
    return config.baseTextures[eyeId] ?? null;
  }

  function estimatePixels(texture: THREE.Texture): number {
    const image = texture.image as { width?: number; height?: number } | undefined;
    const width = typeof image?.width === 'number' ? image.width : 256;
    const height = typeof image?.height === 'number' ? image.height : 256;
    return Math.max(1, width * height);
  }

  function evictIfNeeded(protectedKey?: string): void {
    if (usedPixels <= maxCachedPixels) return;
    const candidates = [...cache.values()]
      .filter((entry) => entry.key !== protectedKey && (retainCounts.get(entry.key) ?? 0) <= 0)
      .sort((a, b) => a.lastUsed - b.lastUsed);
    for (const entry of candidates) {
      if (usedPixels <= maxCachedPixels) break;
      cache.delete(entry.key);
      usedPixels -= entry.pixels;
      entry.texture.dispose();
    }
  }

  async function resolve(eyeId: EyeId, expression: EyeExpression): Promise<THREE.Texture | null> {
    if (disposed) return baseTexture(eyeId);
    if (expression === 'base') return baseTexture(eyeId);

    const cacheKey = key(eyeId, expression);
    const cached = cache.get(cacheKey);
    if (cached != null) {
      cached.lastUsed = ++tick;
      return cached.texture;
    }

    const inflight = pending.get(cacheKey);
    if (inflight != null) return inflight;

    const assetId = eyeExpressionAssetId(eyeId, expression);
    const entry = config.manifest.byId.get(assetId);
    if (entry == null || entry.path === '') {
      return baseTexture(eyeId);
    }

    const promise = config.textureLoader
      .load(runtimeAssetUrl('/' + entry.path))
      .then((texture) => {
        if (disposed) {
          texture.dispose();
          return baseTexture(eyeId);
        }
        const pixels = estimatePixels(texture);
        cache.set(cacheKey, { key: cacheKey, texture, pixels, lastUsed: ++tick });
        usedPixels += pixels;
        evictIfNeeded(cacheKey);
        return texture;
      })
      .catch((error: unknown) => {
        console.warn(`[eye-texture-store] expression load failed: ${assetId}`, error);
        return baseTexture(eyeId);
      })
      .finally(() => {
        pending.delete(cacheKey);
      });
    pending.set(cacheKey, promise);
    return promise;
  }

  function retain(eyeId: EyeId, expression: EyeExpression): () => void {
    if (disposed) return () => {};
    const cacheKey = key(eyeId, expression);
    retainCounts.set(cacheKey, (retainCounts.get(cacheKey) ?? 0) + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const next = (retainCounts.get(cacheKey) ?? 1) - 1;
      if (next <= 0) retainCounts.delete(cacheKey);
      else retainCounts.set(cacheKey, next);
      evictIfNeeded();
    };
  }

  function dispose(): void {
    disposed = true;
    for (const entry of cache.values()) entry.texture.dispose();
    cache.clear();
    pending.clear();
    retainCounts.clear();
    usedPixels = 0;
  }

  return { resolve, retain, dispose };
}
